import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import { computeTrailStartDate } from '../../utils/campLogic';
import { ensureCampUserOwner, resolveCreatedCampUserId } from '../../utils/campUser';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SafeIcon from '../../common/SafeIcon';

const EVERGREEN_AAP_CHECKOUT_URL = 'https://gospelwiseauthor.com/checkout/EVERGREEN_AAP_PRODUCT_ID';
const HUB_WEBHOOK_URL = 'https://gospelwiseauthor.app/gwa-camp-register.php';
const CAMP_APP_URL = 'https://camp.gospelwiseauthor.com';
const HUB_LOGIN_URL = `https://gospelwiseauthor.app/#/login?redirect=${CAMP_APP_URL}`;

export default function Start() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('new');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [evergreenOpen, setEvergreenOpen] = useState(true);
  const [checkingReg, setCheckingReg] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [existingEmail, setExistingEmail] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('settings_gwacamp');
        const data = Array.isArray(res) ? res : res.data || [];
        const setting = data.find((row) => row.key === 'evergreen_registration_open' || row.setting_key === 'evergreen_registration_open');

        if (setting && setting.value === 'false') {
          setEvergreenOpen(false);
        }
      } catch (fetchError) {
        console.error('Failed to check evergreen registration status', fetchError);
      } finally {
        setCheckingReg(false);
      }
    };

    fetchSettings();
  }, []);

  if (user) return <Navigate to="/dashboard" replace />;

  if (checkingReg) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="text-forest-green text-center">
          <SafeIcon name="Loader" className="animate-spin text-4xl mb-4 mx-auto" />
          <p className="font-bold">Opening the camp gate...</p>
        </div>
      </div>
    );
  }

  if (!evergreenOpen) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-4">
        <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-amber py-12">
          <div className="mx-auto w-16 h-16 bg-amber/20 rounded-full flex items-center justify-center mb-4 text-amber">
            <SafeIcon name="Tent" className="text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-2">A Live Camp Is In Session</h1>
          <p className="text-lg text-muted mb-6 leading-relaxed">
            Self-paced registration is paused while a live round of Camp FaiBoWriMo is underway.
          </p>
          <a href="https://join.crossandpen.com/p/camp-faibowrimo/" className="block w-full sm:w-auto mt-6">
            <Button className="py-4 px-10 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold shadow-md">
              See the Live Camp
            </Button>
          </a>
        </Card>
      </div>
    );
  }

  const fireHubWebhook = async (payload) => {
    const secret = import.meta.env.VITE_HUB_WEBHOOK_SECRET || 'gwa_camp_secure_sync_2026_xyz';

    try {
      const res = await fetch(HUB_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret,
          app_slug: 'gwacamp',
          ...payload
        })
      });

      if (!res.ok) return null;

      const data = await res.json();
      if (data?.auth_token) setAuthToken(data.auth_token);
      if (data?.requires_verification) setNeedsVerification(true);

      return data;
    } catch (webhookError) {
      console.warn('Hub webhook failed', webhookError);
      return null;
    }
  };

  const createCampUser = async (payload, email) => {
    const createResponse = await api.post('users_gwacamp', {
      ...payload,
      owner_id: 'pending'
    });
    const userId = await resolveCreatedCampUserId(createResponse, email);

    await api.put('users_gwacamp', userId, { owner_id: userId });

    return userId;
  };

  const handleNewUser = async (event) => {
    event.preventDefault();

    if (!form.name || !form.email || !form.password) {
      setError('Please fill out all fields.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const cleanEmail = form.email.trim().toLowerCase();
      const existingRes = await api.get('users_gwacamp');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];

      if (rows.some((row) => (row.email || '').trim().toLowerCase() === cleanEmail)) {
        setError('That email already has a Camp account.');
        return;
      }

      const trailStart = computeTrailStartDate();

      await createCampUser({
        name: form.name,
        email: cleanEmail,
        role: 'camper',
        pass_type: 'evergreen',
        trail_start_date: trailStart,
        first_login_complete: false
      }, cleanEmail);

      await fireHubWebhook({
        email: cleanEmail,
        name: form.name,
        full_name: form.name,
        password: form.password,
        trail_start_date: trailStart,
        source: 'evergreen_start'
      });

      setRegisteredEmail(cleanEmail);
      setStep(2);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUser = async (event) => {
    event.preventDefault();

    if (!existingEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    const cleanEmail = existingEmail.trim().toLowerCase();

    try {
      const existingRes = await api.get('users_gwacamp');
      const rows = Array.isArray(existingRes) ? existingRes : existingRes.data || [];
      let existing = rows.find((row) => (row.email || '').trim().toLowerCase() === cleanEmail);
      const trailStart = computeTrailStartDate();

      if (!existing) {
        const userId = await createCampUser({
          name: '',
          email: cleanEmail,
          role: 'camper',
          pass_type: 'evergreen',
          trail_start_date: trailStart,
          first_login_complete: false
        }, cleanEmail);

        existing = { id: userId, owner_id: userId };
      } else {
        existing = await ensureCampUserOwner(existing);

        if (existing.pass_type === 'free') {
          await api.put('users_gwacamp', existing.id, {
            pass_type: 'evergreen',
            trail_start_date: trailStart
          });
        }
      }

      const hubResult = await fireHubWebhook({
        email: cleanEmail,
        action: 'grant_access',
        trail_start_date: trailStart,
        source: 'existing_hub_user'
      });

      if (!hubResult?.success) {
        throw new Error('We could not reach the GospelWise Hub. Please try again, or email jeremy@crossandpen.com.');
      }

      setRegisteredEmail(cleanEmail);
      setStep(2);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setLoading(false);
    }
  };

  const enterCamp = () => {
    window.location.href = authToken
      ? `${CAMP_APP_URL}/?token=${encodeURIComponent(authToken)}`
      : HUB_LOGIN_URL;
  };

  const goToAapCheckout = () => {
    window.location.href = `${EVERGREEN_AAP_CHECKOUT_URL}?email=${encodeURIComponent(registeredEmail)}`;
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <Card className="max-w-xl w-full text-center space-y-6 shadow-lg border-tan border-t-4 border-t-forest-green">
        <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mb-4 text-white">
          <SafeIcon name="Compass" className="text-3xl" />
        </div>

        {step === 1 && (
          <>
            <h1 className="text-2xl font-bold text-dark-green">Your Week at Camp Starts Tomorrow</h1>
            <p className="text-muted text-sm">Register in under a minute — Day 1 opens at 7 AM tomorrow morning.</p>

            <div className="flex border-b border-tan mb-6">
              <button className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'new' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted bg-gray-50/50'}`} onClick={() => { setActiveTab('new'); setError(''); }}>New Camper</button>
              <button className={`flex-1 py-3 text-sm font-bold transition-colors ${activeTab === 'existing' ? 'border-b-2 border-forest-green text-forest-green' : 'text-muted bg-gray-50/50'}`} onClick={() => { setActiveTab('existing'); setError(''); }}>I Have a GWA Account</button>
            </div>

            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md mb-4">{error}</div>}

            {activeTab === 'new' ? (
              <form onSubmit={handleNewUser} className="space-y-4 text-left">
                <Input label="Full Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required placeholder="e.g. Jane Doe" />
                <Input label="Email Address" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required placeholder="you@email.com" />
                <Input label="Create a Password" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required placeholder="••••••••" />
                <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>{loading ? 'Opening the gate...' : 'Step Into Camp — Free'}</Button>
              </form>
            ) : (
              <form onSubmit={handleExistingUser} className="space-y-6 text-left">
                <div className="bg-amber/10 p-4 rounded-md border border-amber/30 text-sm text-dark-green">Already part of the GospelWise Author family? Enter your email and we'll set your trail.</div>
                <Input label="Your GWA Email Address" type="email" value={existingEmail} onChange={(event) => setExistingEmail(event.target.value)} required placeholder="you@email.com" />
                <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>{loading ? 'Setting your trail...' : 'Step Into Camp — Free'}</Button>
              </form>
            )}
          </>
        )}

        {step === 2 && (
          <div className="space-y-5 py-2">
            <div className="mx-auto w-16 h-16 bg-forest-green/10 rounded-full flex items-center justify-center">
              <SafeIcon name="Tent" className="text-3xl text-forest-green" />
            </div>
            <h2 className="text-2xl font-bold text-dark-green">Welcome to Camp — You're In!</h2>
            <p className="text-muted leading-relaxed">Today is your prep day. Day 1 of your Cabin Call workbook opens tomorrow morning at 7 AM your local time.</p>

            {needsVerification && (
              <div className="bg-forest-green/10 border border-forest-green/30 rounded-md p-4 text-sm text-dark-green text-left">
                <span className="font-bold">One quick step:</span> check your inbox for a verification email from GospelWise Author and click the link to activate your account. You'll need it to get into camp.
              </div>
            )}

            <div className="bg-amber/10 border border-amber/30 rounded-md p-5 text-left space-y-3">
              <p className="font-bold text-dark-green">One-time offer — the All-Access Pass, just $9 today</p>
              <p className="text-sm text-dark-green leading-relaxed text-xs">Keep everything you build forever plus personal daily emails, the private podcast, and more.</p>
              <Button className="w-full py-3 text-lg bg-amber hover:bg-orange-600 text-dark-green font-bold" onClick={goToAapCheckout}>Add All-Access — $9</Button>
            </div>
            <Button variant="outline" className="w-full py-3 border-2 border-forest-green" onClick={enterCamp}>Continue to Camp — Free</Button>
          </div>
        )}
      </Card>
    </div>
  );
}